// React
import {
	ComponentProps,
	forwardRef,
	useRef,
	useState,
	ComponentRef,
	ForwardRefExoticComponent,
} from "react"

// Icons
import { CheckIcon, FiChevronDown } from "@/lib/icons"

// React Phone Number Input
import * as RPNInput from "react-phone-number-input"
import { getEmojiFlag, type TCountryCode } from "@/lib/countries"

// UI
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command"
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover"
import { ScrollArea } from "@/components/ui/scroll-area"
import { InputProps } from "@/components/form-controls/Input"

// Types
import {
	CoreLocationCountryChoices,
	UsersUserProfileLanguageChoices,
} from "@/api/graphql/types"

// Intentional: EU-only phone numbers.
// We restrict the picker to CoreLocationCountryChoices (EU-27) — no GB, CH,
// NO, US, etc. This is a deliberate product decision: we only accept phone
// numbers from countries we operate in. Investors with non-EU mobiles are
// out of scope. Do not widen this list or add `addInternationalOption`
// without a product sign-off — see PR #645 review discussion.
const ALLOWED_COUNTRIES = Object.values(
	CoreLocationCountryChoices,
) as RPNInput.Country[]

type PhoneInputProps = Omit<
	ComponentProps<"input">,
	"onChange" | "value" | "ref"
> &
	Omit<RPNInput.Props<typeof RPNInput.default>, "onChange">

/**
 * PhoneInput
 *
 * Based on: https://github.com/omeralpi/shadcn-phone-input
 * DOCS: https://shadcn-phone-input.vercel.app/#setup
 */
const PhoneInput: ForwardRefExoticComponent<PhoneInputProps> = forwardRef<
	ComponentRef<typeof RPNInput.default>,
	PhoneInputProps
>(
	(
		{
			className, // @ts-expect-error - onChange type excluded from PhoneInputProps but needed at runtime
			onChange,
			value,

			// Set default country to NL
			defaultCountry = UsersUserProfileLanguageChoices.Nl,
			...props
		},
		ref,
	) => {
		return (
			<RPNInput.default
				ref={ref}
				className={cn("flex", className)}
				flagComponent={FlagComponent}
				countrySelectComponent={CountrySelect}
				inputComponent={InputComponent}
				smartCaret={false}
				value={value ?? undefined}
				defaultCountry={defaultCountry}
				countries={ALLOWED_COUNTRIES}
				/**
				 * Handles the onChange event.
				 *
				 * react-phone-number-input might trigger the onChange event as undefined
				 * when a valid phone number is not entered. To prevent this,
				 * the value is coerced to an empty string.
				 *
				 * @param {E164Number | undefined} value - The entered value
				 */
				onChange={(value) =>
					// oxlint-disable-next-line typescript/no-unsafe-return
					onChange?.(value ?? ("" as RPNInput.Value))
				}
				{...props}
			/>
		)
	},
)
PhoneInput.displayName = "PhoneInput"

const InputComponent = forwardRef<HTMLInputElement, InputProps>(
	({ className, showIcon, hasError, ...props }, ref) => (
		<input
			type={props.type ?? "text"}
			ref={ref}
			{...props}
			className={cn(
				"rounded-s-none rounded-e-lg",
				className,
				"appearance-none",
				showIcon === "bs-currency-euro" && "pl-8", // Padding left Because of the euro sign
				"border",
				hasError ? `border-red-300` : `border-gray-300`,
				hasError && `text-red-600`,
				"shadow-xs",
				"placeholder-gray-400",
				"focus-within:text-dark focus:outline-hidden",
				hasError ? `focus:ring-red-500` : `focus:ring-primary-500`,
				hasError ? `focus:border-red-500` : `focus:border-primary-500`,
				"text-sm disabled:bg-gray-100",
			)}
		/>
	),
)
InputComponent.displayName = "InputComponent"

type CountryEntry = { label: string; value: RPNInput.Country | undefined }

type CountrySelectProps = {
	disabled?: boolean
	value: RPNInput.Country
	options: CountryEntry[]
	onChange: (country: RPNInput.Country) => void
}

const CountrySelect = ({
	disabled,
	value: selectedCountry,
	options: countryList,
	onChange,
}: CountrySelectProps) => {
	const scrollAreaRef = useRef<HTMLDivElement>(null)
	const [searchValue, setSearchValue] = useState("")
	const [isOpen, setIsOpen] = useState(false)

	return (
		<Popover open={isOpen} onOpenChange={setIsOpen} modal>
			<PopoverTrigger asChild>
				<Button
					type="button"
					variant="outline"
					className={cn(
						"flex gap-1 rounded-s-lg rounded-e-none border-r-0 px-3 focus:z-10",
						"appearance-none",
						"border",
						`border-gray-300`,
						"shadow-xs",
						"placeholder-gray-400",
						"focus-within:text-dark focus:outline-hidden",
						`focus:ring-primary-500`,
						`focus:border-primary-500`,
						"text-sm disabled:bg-gray-100",
					)}
					disabled={disabled}
				>
					<FlagComponent country={selectedCountry} />
					<FiChevronDown
						className={cn(
							"-mr-2 size-4 opacity-50",
							disabled ? "hidden" : "opacity-100",
						)}
					/>
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-[300px] p-0">
				<Command>
					<CommandInput
						value={searchValue}
						onValueChange={(value) => {
							setSearchValue(value)
							setTimeout(() => {
								if (scrollAreaRef.current) {
									const viewportElement =
										scrollAreaRef.current.querySelector(
											"[data-radix-scroll-area-viewport]",
										)
									if (viewportElement) {
										viewportElement.scrollTop = 0
									}
								}
							}, 0)
						}}
						placeholder="Search country..."
					/>
					<CommandList>
						<ScrollArea ref={scrollAreaRef} className="h-72">
							<CommandEmpty>No country found.</CommandEmpty>
							<CommandGroup>
								{countryList.map(({ value, label }) =>
									value ? (
										<CountrySelectOption
											key={value}
											country={value}
											countryName={label}
											selectedCountry={selectedCountry}
											onChange={onChange}
											onSelectComplete={() =>
												setIsOpen(false)
											}
										/>
									) : null,
								)}
							</CommandGroup>
						</ScrollArea>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	)
}

interface CountrySelectOptionProps extends RPNInput.FlagProps {
	selectedCountry: RPNInput.Country
	onChange: (country: RPNInput.Country) => void
	onSelectComplete: () => void
}

const CountrySelectOption = ({
	country,
	countryName,
	selectedCountry,
	onChange,
	onSelectComplete,
}: CountrySelectOptionProps) => {
	const handleSelect = () => {
		onChange(country)
		onSelectComplete()
	}

	return (
		<CommandItem className="gap-2" onSelect={handleSelect}>
			<FlagComponent country={country} />
			<span className="flex-1 text-sm">{countryName}</span>
			<span className="text-foreground/50 text-sm">{`+${RPNInput.getCountryCallingCode(country)}`}</span>
			<CheckIcon
				className={`ml-auto size-4 ${country === selectedCountry ? "opacity-100" : "opacity-0"}`}
			/>
		</CommandItem>
	)
}

// FlagComponent
//
// react-phone-number-input's `Country` is a strict subset of countries-list's
// `TCountryCode`, so this cast is safe today. The cast keeps us honest: if a
// future countries-list bump drops a code that libphonenumber-js still emits,
// the cast becomes a type error instead of a silent empty flag.
const FlagComponent = ({ country }: { country: RPNInput.Country }) => {
	return (
		<span className="flex text-xl">
			{getEmojiFlag(country as TCountryCode)}
		</span>
	)
}

// Export
export { PhoneInput }
